import React from 'react';
import './forms.css';

/**
 * A row of stars a visitor gives, drawn as what it actually is: a group of radio buttons.
 *
 * Nothing here listens for a key. A set of native radios sharing one name already answers the arrow
 * keys, already takes one tab stop rather than five, and is already announced as "3 of 5" — so the
 * inputs are moved out of sight and the glyph beside each one is what a visitor sees. A rating that
 * only a mouse can give is one a person using a keyboard cannot leave, and this platform's reviews
 * are worth more than a `div` with an `onClick`.
 *
 * The read-only row is one image with one label, never five focusable things a reader must walk past.
 */

/** The glyph a star is drawn with; lit and unlit are the same shape, so the row never changes width. */
const STAR = '★';

/** A store's languages, which a star is read out in. */
export type StarLanguage = 'ar' | 'fr' | 'en';

/** How a screen reader names one star being given, and a score being shown, in each language. */
const STAR_WORDS: Readonly<Record<StarLanguage, { star: (n: number, of: number) => string; score: (n: number, of: number) => string }>> = {
  en: { star: (n, of) => `${n} of ${of}`, score: (n, of) => `${n} out of ${of}` },
  fr: { star: (n, of) => `${n} sur ${of}`, score: (n, of) => `${n} sur ${of}` },
  ar: { star: (n, of) => `${n} من ${of}`, score: (n, of) => `${n} من ${of}` }
};

// Answers the words a star row is read out in: the store's language, English when it named none.
export const starWords = (language: StarLanguage | undefined) => STAR_WORDS[language ?? 'en'];

/** What a rating row needs, whether it is being given or only shown. */
export type StarRatingProps = {
  /** The radio group's name; every row on a page needs its own, or two rows share one answer. */
  name: string;
  value: number;
  max: number;
  label: string;
  /** Absent for a row that only shows a score, which is then drawn as one image rather than as inputs. */
  onChange?: (value: number) => void;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
  /** The store's language, so "4 sur 5" is what a French shopper's screen reader says. */
  language?: StarLanguage;
};

// Counts the stars a row draws, keeping a merchant's typo from asking a page for a hundred of them.
export const starCount = (max: number): number => Math.min(Math.max(Math.trunc(max) || 5, 1), 10);

// Reads a score into the row it is drawn on, so nothing outside the row can light a star that is not there.
export const starScore = (value: number, max: number): number => Math.min(Math.max(Math.round(value) || 0, 0), max);

// Draws one star: the radio a keyboard reaches it by, and the glyph a visitor sees in its place.
const Star: React.FC<{ index: number; row: StarRatingProps; count: number; lit: boolean }> = ({ index, row, count, lit }) => (
  <label className="xs-form-star" data-lit={lit ? 'yes' : 'no'}>
    <input
      className="xs-form-star-input"
      type="radio"
      name={row.name}
      value={index}
      checked={starScore(row.value, count) === index}
      aria-label={starWords(row.language).star(index, count)}
      onChange={() => row.onChange?.(index)}
    />
    <span className="xs-form-star-mark" aria-hidden="true">{STAR}</span>
  </label>
);

// Draws a score nobody is being asked for: one image, one label, and no tab stop of any kind.
const StarsShown: React.FC<{ row: StarRatingProps; count: number }> = ({ row, count }) => (
  <span className={row.className ? `xs-form-stars ${row.className}` : 'xs-form-stars'} data-ask="no"
    role="img" aria-label={starWords(row.language).score(starScore(row.value, count), count)}>
    {Array.from({ length: count }, (_, at) => (
      <span className="xs-form-star" data-lit={at < starScore(row.value, count) ? 'yes' : 'no'} key={at}>
        <span className="xs-form-star-mark" aria-hidden="true">{STAR}</span>
      </span>
    ))}
  </span>
);

// Draws the row: the stars being asked for, or the score being shown when nobody is asking.
export const StarRating: React.FC<StarRatingProps> = (row) => {
  const count = starCount(row.max);
  if (!row.onChange) return <StarsShown row={row} count={count} />;
  return (
    <span
      className={row.className ? `xs-form-stars ${row.className}` : 'xs-form-stars'}
      data-ask="yes"
      role="radiogroup"
      aria-label={row.label}
      aria-invalid={row.invalid ? true : undefined}
      aria-describedby={row.describedBy}
    >
      {Array.from({ length: count }, (_, at) => (
        <Star key={at} index={at + 1} row={row} count={count} lit={at < starScore(row.value, count)} />
      ))}
    </span>
  );
};
